/* eslint-disable unicorn/prefer-module */
import { useMemo, useState } from "react";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { BadgeDato, CategoriaLeyendaIpsos, Loader } from "..";
import { ipsos } from "../../data/ipsos2025";
import {
  ESCENARIOS_CAPTACION_DELIVERY,
  IPSOS_DELIVERY,
  TICKETS_PROMEDIO_DELIVERY,
} from "../../data/ipsos2025/delivery";
import { useCategoriaMoneda, useUniversoCategorias } from "../../utils";
import { Header } from "../Header";
import styles from "./CategoriaDelivery.module.scss";

// Apps disponibles para simulación
const APPS_SIMULABLES = Object.keys(IPSOS_DELIVERY.apps);

export const Delivery = () => {
  const { formatNumber, formatLocal, convertCLP, pais } = useCategoriaMoneda();
  const {
    edificios,
    loading,
    error,
    checkAll,
    checkedBuildings,
    toggleCheck,
    toggleCheckAll,
    cantidadEdificios,
    totalPantallas,
    totalEjecutivos,
    totalEmpresas,
  } = useUniversoCategorias(pais);
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [showOportunidadModal, setShowOportunidadModal] = useState(false);
  const [appSeleccionada, setAppSeleccionada] = useState("Uber Eats");
  const [porcentajeCaptacion, setPorcentajeCaptacion] = useState(10);
  const [ticketPromedio, setTicketPromedio] = useState(TICKETS_PROMEDIO_DELIVERY[1].valor);
  const [verAnual, setVerAnual] = useState(false);

  // Calcular mercado con IPSOS
  const resultados = useMemo(() => {
    if (!totalEjecutivos) return null;

    // Usuarios de delivery
    const usuariosDelivery = Math.round(totalEjecutivos * IPSOS_DELIVERY.usaDelivery.valor);
    const noUsuarios = Math.round(totalEjecutivos * IPSOS_DELIVERY.noUsaDelivery.valor);

    // Calcular pedidos mensuales según frecuencia
    const { frecuenciaUso, apps } = IPSOS_DELIVERY;
    let pedidosMensuales = 0;

    Object.values(frecuenciaUso).forEach(({ pct, factor }) => {
      pedidosMensuales += Math.round(usuariosDelivery * pct.valor * factor.valor);
    });

    // Mercado mensual y anual
    const mercadoMensual = pedidosMensuales * ticketPromedio;
    const mercadoAnual = mercadoMensual * 12;

    // Desglose por frecuencia — pct agregado sigue siendo Ipsos (suma de tramos reales, sin supuesto)
    const altaFrecuenciaPct =
      frecuenciaUso.todosLosDias.pct.valor +
      frecuenciaUso.finesDeSemana.pct.valor +
      frecuenciaUso.variosDiasSemana.pct.valor +
      frecuenciaUso.unaVezSemana.pct.valor;
    const mediaFrecuenciaPct = frecuenciaUso.cadaDosSemananas.pct.valor + frecuenciaUso.cadaTresSemanas.pct.valor;
    const bajaFrecuenciaPct = frecuenciaUso.unaVezMes.pct.valor + frecuenciaUso.menosDeUnaVezMes.pct.valor;

    const desgloseFrecuencia = {
      altaFrecuencia: {
        // 1+ vez/semana: todos los días + fines de semana + varios días + 1 vez/semana
        pct: altaFrecuenciaPct,
        pctDato: ipsos(altaFrecuenciaPct, {
          lamina: 68,
          pregunta: "¿Con qué frecuencia pide delivery?",
          base: 311,
          nota: "suma de 4 tramos Ipsos (1+ vez/semana)",
        }),
        personas: Math.round(usuariosDelivery * altaFrecuenciaPct),
        pedidos: Math.round(
          usuariosDelivery * frecuenciaUso.todosLosDias.pct.valor * frecuenciaUso.todosLosDias.factor.valor +
            usuariosDelivery * frecuenciaUso.finesDeSemana.pct.valor * frecuenciaUso.finesDeSemana.factor.valor +
            usuariosDelivery * frecuenciaUso.variosDiasSemana.pct.valor * frecuenciaUso.variosDiasSemana.factor.valor +
            usuariosDelivery * frecuenciaUso.unaVezSemana.pct.valor * frecuenciaUso.unaVezSemana.factor.valor
        ),
      },
      mediaFrecuencia: {
        // Cada 2-3 semanas
        pct: mediaFrecuenciaPct,
        pctDato: ipsos(mediaFrecuenciaPct, {
          lamina: 68,
          pregunta: "¿Con qué frecuencia pide delivery?",
          base: 311,
          nota: "suma de 2 tramos Ipsos (cada 2-3 semanas)",
        }),
        personas: Math.round(usuariosDelivery * mediaFrecuenciaPct),
        pedidos: Math.round(
          usuariosDelivery * frecuenciaUso.cadaDosSemananas.pct.valor * frecuenciaUso.cadaDosSemananas.factor.valor +
            usuariosDelivery * frecuenciaUso.cadaTresSemanas.pct.valor * frecuenciaUso.cadaTresSemanas.factor.valor
        ),
      },
      bajaFrecuencia: {
        // 1 vez/mes o menos
        pct: bajaFrecuenciaPct,
        pctDato: ipsos(bajaFrecuenciaPct, {
          lamina: 68,
          pregunta: "¿Con qué frecuencia pide delivery?",
          base: 311,
          nota: "suma de 2 tramos Ipsos (1 vez/mes o menos)",
        }),
        personas: Math.round(usuariosDelivery * bajaFrecuenciaPct),
        pedidos: Math.round(
          usuariosDelivery * frecuenciaUso.unaVezMes.pct.valor * frecuenciaUso.unaVezMes.factor.valor +
            usuariosDelivery * frecuenciaUso.menosDeUnaVezMes.pct.valor * frecuenciaUso.menosDeUnaVezMes.factor.valor
        ),
      },
    };

    // Market share por app
    const marketShareApps = {};
    Object.entries(apps).forEach(([app, dato]) => {
      marketShareApps[app] = {
        dato,
        share: dato.valor,
        usuarios: Math.round(usuariosDelivery * dato.valor),
        pedidos: Math.round(pedidosMensuales * dato.valor),
        mercadoMensual: Math.round(mercadoMensual * dato.valor),
      };
    });

    return {
      usuariosDelivery,
      noUsuarios,
      pedidosMensuales,
      pedidosAnuales: pedidosMensuales * 12,
      mercadoMensual,
      mercadoAnual,
      desgloseFrecuencia,
      marketShareApps,
    };
  }, [totalEjecutivos, ticketPromedio]);

  if (loading) return <Loader />;
  if (error) return <p className={styles.disclaimer}>{error}</p>;

  return (
    <div className={styles.deliveryPage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>DELIVERY</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <CategoriaLeyendaIpsos />

      <div className={styles.deliveryContent}>
        {/* Tabla integrada: uso + frecuencia */}
        {resultados && (
          <div className={styles.tablaResultadosContainer}>
            <table className={cx("table table-sm", styles.tablaResultados, styles.tablaIntegrada)}>
              <thead className="table-dark">
                <tr>
                  <th className="text-center">No usan delivery</th>
                  <th className="text-center">Sí usan delivery</th>
                  <th className="text-center">Frecuencia de uso</th>
                  <th className="text-center">Personas</th>
                  <th className="text-center">
                    <div className={styles.headerConsumos}>
                      <span>Pedidos</span>
                      <div className={styles.toggleContainer}>
                        <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>mes</span>
                        <label className={styles.toggleSwitch}>
                          <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                          <span className={styles.toggleSlider} />
                        </label>
                        <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>año</span>
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td rowSpan={3} className={cx("text-center align-middle", styles.celdaNoUsa)}>
                    <div className={styles.celdaContenido}>
                      <BadgeDato dato={IPSOS_DELIVERY.noUsaDelivery}>
                        {(IPSOS_DELIVERY.noUsaDelivery.valor * 100).toFixed(0)}%
                      </BadgeDato>
                      <div className={styles.valorGrande}>{formatNumber(resultados.noUsuarios)}</div>
                    </div>
                  </td>
                  <td rowSpan={3} className={cx("text-center align-middle", styles.celdaSiUsa)}>
                    <div className={styles.celdaContenido}>
                      <BadgeDato dato={IPSOS_DELIVERY.usaDelivery}>
                        {(IPSOS_DELIVERY.usaDelivery.valor * 100).toFixed(0)}%
                      </BadgeDato>
                      <div className={styles.valorGrande}>{formatNumber(resultados.usuariosDelivery)}</div>
                    </div>
                  </td>
                  <td className={styles.celdaFrecuencia}>
                    <BadgeDato dato={resultados.desgloseFrecuencia.altaFrecuencia.pctDato}>
                      {(resultados.desgloseFrecuencia.altaFrecuencia.pct * 100).toFixed(0)}%
                    </BadgeDato>
                    <span className={styles.frecuenciaTexto}>Alta: 1 o más veces por semana</span>
                  </td>
                  <td className="text-center">{formatNumber(resultados.desgloseFrecuencia.altaFrecuencia.personas)}</td>
                  <td className="text-center">
                    {formatNumber(
                      verAnual
                        ? resultados.desgloseFrecuencia.altaFrecuencia.pedidos * 12
                        : resultados.desgloseFrecuencia.altaFrecuencia.pedidos
                    )}
                  </td>
                </tr>
                <tr>
                  <td className={styles.celdaFrecuencia}>
                    <BadgeDato dato={resultados.desgloseFrecuencia.mediaFrecuencia.pctDato}>
                      {(resultados.desgloseFrecuencia.mediaFrecuencia.pct * 100).toFixed(0)}%
                    </BadgeDato>
                    <span className={styles.frecuenciaTexto}>Media: cada 2-3 semanas</span>
                  </td>
                  <td className="text-center">
                    {formatNumber(resultados.desgloseFrecuencia.mediaFrecuencia.personas)}
                  </td>
                  <td className="text-center">
                    {formatNumber(
                      verAnual
                        ? resultados.desgloseFrecuencia.mediaFrecuencia.pedidos * 12
                        : resultados.desgloseFrecuencia.mediaFrecuencia.pedidos
                    )}
                  </td>
                </tr>
                <tr>
                  <td className={styles.celdaFrecuencia}>
                    <BadgeDato dato={resultados.desgloseFrecuencia.bajaFrecuencia.pctDato}>
                      {(resultados.desgloseFrecuencia.bajaFrecuencia.pct * 100).toFixed(0)}%
                    </BadgeDato>
                    <span className={styles.frecuenciaTexto}>Baja: 1 vez al mes o menos</span>
                  </td>
                  <td className="text-center">{formatNumber(resultados.desgloseFrecuencia.bajaFrecuencia.personas)}</td>
                  <td className="text-center">
                    {formatNumber(
                      verAnual
                        ? resultados.desgloseFrecuencia.bajaFrecuencia.pedidos * 12
                        : resultados.desgloseFrecuencia.bajaFrecuencia.pedidos
                    )}
                  </td>
                </tr>
                <tr className={styles.filaTotalPedidos}>
                  <td colSpan={4} className={styles.celdaVacia} />
                  <td className={cx("text-center", styles.celdaTotalPedidos)}>
                    <strong>{formatNumber(verAnual ? resultados.pedidosAnuales : resultados.pedidosMensuales)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Ticket + Mercado */}
            <div className={styles.dosBloquesContainer}>
              {/* Ticket promedio */}
              <div className={styles.bloqueTicket}>
                <div className={styles.ticketLabel}>Ticket promedio (supuesto)</div>
                <div className={styles.ticketBox}>
                  <div className={styles.ticketOpciones}>
                    {TICKETS_PROMEDIO_DELIVERY.map((t) => (
                      <label key={t.valor} className={styles.ticketRadio}>
                        <input
                          type="radio"
                          name="ticketPromedio"
                          checked={ticketPromedio === t.valor}
                          onChange={() => setTicketPromedio(t.valor)}
                        />
                        <span>{formatLocal(convertCLP(t.valor))}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Mercado */}
              <div className={styles.bloqueMercado}>
                <div className={styles.mercadoLabel}>Mercado {verAnual ? "anual" : "mensual"}</div>
                <div className={styles.mercadoValorGrande}>
                  {formatLocal(convertCLP(verAnual ? resultados.mercadoAnual : resultados.mercadoMensual))}
                </div>
              </div>
            </div>

            {/* Market Share por App */}
            <div className={styles.marketShareContainer}>
              <h3 className={styles.marketShareTitulo}>Market Share por App</h3>
              <div className={styles.appsGrid}>
                {Object.entries(resultados.marketShareApps).map(([app, data]) => {
                  const colorClass =
                    app === "Uber Eats" ? styles.uberEats : app === "Pedidos Ya" ? styles.pedidosYa : styles.rappiCard;
                  return (
                    <div key={app} className={cx(styles.appCard, colorClass)}>
                      <div className={styles.appNombre}>{app}</div>
                      <div className={styles.appShare}>
                        <BadgeDato dato={data.dato}>{(data.share * 100).toFixed(0)}%</BadgeDato>
                      </div>
                      <div className={styles.appMetricas}>
                        <div>
                          <span className={styles.metricaLabel}>Usuarios</span>
                          <span className={styles.metricaValor}>{formatNumber(data.usuarios)}</span>
                        </div>
                        <div>
                          <span className={styles.metricaLabel}>Pedidos/{verAnual ? "año" : "mes"}</span>
                          <span className={styles.metricaValor}>
                            {formatNumber(verAnual ? data.pedidos * 12 : data.pedidos)}
                          </span>
                        </div>
                        <div>
                          <span className={styles.metricaLabel}>Mercado/{verAnual ? "año" : "mes"}</span>
                          <span className={styles.metricaValor}>
                            {formatLocal(convertCLP(verAnual ? data.mercadoMensual * 12 : data.mercadoMensual))}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button type="button" className={styles.simularBtn} onClick={() => setShowOportunidadModal(true)}>
                Simular Oportunidad
              </button>
            </div>

            {/* Disclaimer */}
            <div className={styles.disclaimer}>
              <p>
                <strong>Ipsos 2025 (lámina 68, base 400):</strong> perfil: 50,4% solteros, 24,3% vive solo, 38,9% vive
                en pareja. Zona residencial: 16,2% Las Condes, 10,9% Santiago, 8,1% Ñuñoa, 7,7% La Florida (mayoría
                sector oriente). Gasto promedio mensual en delivery (resumen Ipsos): {formatLocal(convertCLP(80000))} -{" "}
                {formatLocal(convertCLP(150000))}.
              </p>
              <p>
                <strong>Supuestos comerciales:</strong> ticket promedio y pedidos por mes por frecuencia.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modal Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Inventario de Edificios</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                &times;
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> ejecutivos
                </span>
                <button type="button" className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-sm table-hover">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">✓</th>
                    <th className="text-center">Edificio</th>
                    <th className="text-center">Ubicación</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(({ id, edificio, ubicacion, personas }) => (
                    <tr
                      key={id}
                      onClick={() => toggleCheck(id)}
                      className={cx(checkedBuildings[id] && "table-success")}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                      </td>
                      <td className="text-center">{edificio.toUpperCase()}</td>
                      <td className="text-center">{ubicacion}</td>
                      <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Oportunidad */}
      {showOportunidadModal && resultados && (
        <div className={styles.modalOverlay} onClick={() => setShowOportunidadModal(false)}>
          <div className={cx(styles.modalContent, styles.modalOportunidad)} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Simulador de Oportunidad - Delivery</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowOportunidadModal(false)}>
                &times;
              </button>
            </div>
            <div className={styles.modalBody}>
              {/* Selector de App */}
              <div className={styles.appSelector}>
                <label>App objetivo:</label>
                <select value={appSeleccionada} onChange={(e) => setAppSeleccionada(e.target.value)}>
                  {APPS_SIMULABLES.map((app) => (
                    <option key={app} value={app}>
                      {app}
                    </option>
                  ))}
                </select>
              </div>

              {/* Market share actual */}
              <div className={styles.marketShareActual}>
                <h4>Market Share Actual en Edificios</h4>
                <div className={styles.appsGrid}>
                  {Object.entries(IPSOS_DELIVERY.apps).map(([app, dato]) => (
                    <div
                      key={app}
                      className={cx(
                        styles.appItem,
                        app === appSeleccionada && styles.selected,
                        app !== appSeleccionada && styles.appBlur
                      )}
                    >
                      <span className={styles.appNombre}>{app}</span>
                      <span>{(dato.valor * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Oportunidades */}
              <div className={styles.oportunidadesLista}>
                <h4>Oportunidades para {appSeleccionada}</h4>
                <ul>
                  <li>
                    <strong>
                      {formatNumber(
                        resultados.usuariosDelivery - (resultados.marketShareApps[appSeleccionada]?.usuarios || 0)
                      )}
                    </strong>{" "}
                    usuarios usan otras apps de delivery
                  </li>
                  <li>
                    <strong>
                      {formatLocal(
                        convertCLP(
                          resultados.mercadoMensual - (resultados.marketShareApps[appSeleccionada]?.mercadoMensual || 0)
                        )
                      )}
                    </strong>{" "}
                    de mercado mensual en competidores
                  </li>
                  <li>
                    <strong>{formatNumber(resultados.noUsuarios)}</strong> ejecutivos no usan delivery (potencial de
                    activación)
                  </li>
                </ul>
              </div>

              {/* Escenarios de captación */}
              <div className={styles.escenarios}>
                <h4>Escenarios de Captación</h4>
                {ESCENARIOS_CAPTACION_DELIVERY.map((p) => {
                  const pct = p.valor;
                  const usuariosOtras =
                    resultados.usuariosDelivery - (resultados.marketShareApps[appSeleccionada]?.usuarios || 0);
                  const captados = Math.round(usuariosOtras * (pct / 100));
                  const pedidosCaptados = Math.round(
                    (resultados.pedidosMensuales - (resultados.marketShareApps[appSeleccionada]?.pedidos || 0)) *
                      (pct / 100)
                  );
                  const mercadoGanado = pedidosCaptados * ticketPromedio;

                  return (
                    <div
                      key={pct}
                      className={cx(styles.escenarioItem, porcentajeCaptacion === pct && styles.escenarioActivo)}
                      onClick={() => setPorcentajeCaptacion(pct)}
                    >
                      <div className={styles.escenarioLabel}>Captar {pct}% de usuarios de otras apps</div>
                      <div className={styles.escenarioValores}>
                        <div>
                          <span className={styles.valorLabel}>Nuevos usuarios</span>
                          <span className={styles.valor}>{formatNumber(captados)}</span>
                        </div>
                        <div>
                          <span className={styles.valorLabel}>Pedidos captados/mes</span>
                          <span className={styles.valor}>{formatNumber(pedidosCaptados)}</span>
                        </div>
                        <div>
                          <span className={styles.valorLabel}>Mercado ganado/mes</span>
                          <span className={styles.valor}>{formatLocal(convertCLP(mercadoGanado))}</span>
                        </div>
                        <div>
                          <span className={styles.valorLabel}>Mercado ganado/año</span>
                          <span className={styles.valorDestacado}>{formatLocal(convertCLP(mercadoGanado * 12))}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowOportunidadModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Delivery;
